import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errorMessage } from "../../api/client";

export const documentKeys = {
  list: (tenantId: string, projectId: string) =>
    ["tenants", tenantId, "projects", projectId, "documents"] as const,
  detail: (tenantId: string, projectId: string, docId: string) =>
    [...documentKeys.list(tenantId, projectId), docId] as const,
};

export function useDocuments(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: documentKeys.list(tenantId, projectId),
    queryFn: async () => {
      const res = await api.documents.list({
        params: { tenantId, projectId },
        query: { limit: 100, offset: 0 },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useDocument(tenantId: string, projectId: string, docId: string) {
  return useQuery({
    queryKey: documentKeys.detail(tenantId, projectId, docId),
    queryFn: async () => {
      const res = await api.documents.get({ params: { tenantId, projectId, docId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.document;
    },
  });
}

export function useDocumentSegments(tenantId: string, projectId: string, docId: string) {
  return useQuery({
    queryKey: [...documentKeys.detail(tenantId, projectId, docId), "segments"],
    queryFn: async () => {
      const res = await api.documents.segments({ params: { tenantId, projectId, docId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.segments;
    },
  });
}

export function useDocumentAssets(tenantId: string, projectId: string, docId: string) {
  return useQuery({
    queryKey: [...documentKeys.detail(tenantId, projectId, docId), "assets"],
    queryFn: async () => {
      const res = await api.documents.assets({ params: { tenantId, projectId, docId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.assets;
    },
  });
}

export function useDocumentChunks(tenantId: string, projectId: string, docId: string) {
  return useQuery({
    queryKey: [...documentKeys.detail(tenantId, projectId, docId), "chunks"],
    queryFn: async () => {
      const res = await api.documents.chunks({ params: { tenantId, projectId, docId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.chunks;
    },
  });
}

export function useDocumentProcessingSteps(tenantId: string, projectId: string, docId: string) {
  return useQuery({
    queryKey: [...documentKeys.detail(tenantId, projectId, docId), "processing-steps"],
    queryFn: async () => {
      const res = await api.documents.processingSteps({ params: { tenantId, projectId, docId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.steps;
    },
  });
}

/**
 * The three-call upload: presign, direct PUT to storage, complete. The PUT is
 * a plain fetch on purpose — the presigned URL is S3, and sending our bearer
 * header there would be sending it to the wrong service. Parsing starts when
 * `complete` lands; the invalidated listing then shows `processingStatus`.
 */
export function useUploadDocument(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const presigned = await api.documents.uploadUrl({
        params: { tenantId, projectId },
        body: {
          filename: file.name,
          mimeType: file.type || "application/octet-stream",
          sizeBytes: file.size,
        },
      });
      if (presigned.status !== 201) throw new Error(errorMessage(presigned));

      const put = await fetch(presigned.body.uploadUrl, {
        method: "PUT",
        headers: { "content-type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!put.ok) throw new Error(`The upload didn't finish (${put.status}).`);

      const completed = await api.documents.completeUpload({
        params: { tenantId, projectId, docId: presigned.body.documentId },
        body: {},
      });
      if (completed.status !== 200) throw new Error(errorMessage(completed));
      return completed.body.document;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: documentKeys.list(tenantId, projectId) }),
  });
}
